import type { TFunction } from "i18next";
import type { PersonaStatus } from "../types/personas.types";

export type PersonaStatusBadgeVariant = "success" | "new" | "error";

/** Shared chip geometry — DESIGN.md `badge-status-*`: 6px radius, 6px horizontal padding. */
export const PERSONA_STATUS_BADGE_BASE =
  "inline-flex shrink-0 items-center rounded-md px-1.5 py-[3px] text-[10px] font-medium leading-none";

/**
 * Chip treatment for SUGGESTED — the one status that owes the user an action, so it
 * borrows the primary-subtle fill from `PersonasNewAvailableBanner` rather than sitting
 * in neutral slate alongside the two resolved states.
 */
export const PERSONA_NEW_STATUS_CHIP_CLASS =
  "gap-1 bg-primary-subtle font-semibold text-primary-subtle-foreground ring-1 ring-inset ring-primary/25";

/** `bg-current` keeps the dot on the label's exact blue; `bg-primary` is a lighter mismatch. */
export const PERSONA_NEW_STATUS_DOT_CLASS =
  "inline-block size-1 shrink-0 rounded-full bg-current";

export function personaStatusIsAwaitingReview(status: PersonaStatus): boolean {
  return status === "SUGGESTED";
}

/** Library-surface status label — Approved / New / Rejected. Wizard pills stay separate. */
export function personaStatusLabel(t: TFunction, status: PersonaStatus): string {
  switch (status) {
    case "ACTIVE":
      return t("personas.status.approved", "Approved");
    case "SUGGESTED":
      return t("personas.status.new", "New");
    case "REJECTED":
      return t("personas.status.rejected", "Rejected");
  }
}

/** Maps library status onto the chip treatments StatusPill already uses. */
export function personaStatusBadgeVariant(status: PersonaStatus): PersonaStatusBadgeVariant {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "SUGGESTED":
      return "new";
    case "REJECTED":
      return "error";
  }
}

/**
 * The `*-subtle` fills are near-white, so on a white card the chips read as bare
 * colored text. An inset ring gives each one an edge without darkening the fill.
 */
export function personaStatusBadgeClassName(status: PersonaStatus): string {
  const variant = personaStatusBadgeVariant(status);
  switch (variant) {
    case "success":
      return "bg-success-subtle text-success-subtle-foreground ring-1 ring-inset ring-success-border/60";
    case "new":
      return PERSONA_NEW_STATUS_CHIP_CLASS;
    case "error":
      return "bg-error-subtle text-error-subtle-foreground ring-1 ring-inset ring-error-border/60";
  }
}
